import { createContext, useContext } from "react";

// Seasonal themes layer a festive accent palette on top of the base
// light/dark theme (which stays managed by next-themes). A season only
// overrides a small set of semantic color variables (primary, accent, ring
// and their chart/sidebar counterparts) so the rest of the design system,
// contrast and readability are preserved in both light and dark mode.

export const SEASONS = [
  "none",
  "summer",
  "autumn",
  "winter",
  "christmas",
  "newyear",
  "halloween",
  "valentine",
  "anniversary",
  "blackfriday",
  "bluemonday",
] as const;

export type Season = (typeof SEASONS)[number];

// The exact set of CSS custom properties a seasonal palette controls. Kept
// explicit so the provider can cleanly clear them when a season is removed.
export type SeasonalVars = {
  "--season-gradient-end": string;
  "--primary": string;
  "--primary-foreground": string;
  "--accent": string;
  "--accent-foreground": string;
  "--ring": string;
  "--chart-1": string;
  "--chart-2": string;
  "--sidebar-primary": string;
  "--sidebar-primary-foreground": string;
  "--sidebar-ring": string;
};

export const SEASONAL_VAR_KEYS = [
  "--season-gradient-end",
  "--primary",
  "--primary-foreground",
  "--accent",
  "--accent-foreground",
  "--ring",
  "--chart-1",
  "--chart-2",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-ring",
] as const;

// Build a full SeasonalVars object from a compact palette (primary, accent and
// their foregrounds), deriving the chart/sidebar variables from primary/accent.
function palette(input: {
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
}): SeasonalVars {
  return {
    // Keep both ends readable: dark lettering gets a lighter second stop.
    "--season-gradient-end": `color-mix(in oklch, var(--primary) 82%, ${Number(input.primaryForeground.match(/[\d.]+/)?.[0]) < 0.5 ? "white" : "black"})`,
    "--primary": input.primary,
    "--primary-foreground": input.primaryForeground,
    "--accent": input.accent,
    "--accent-foreground": input.accentForeground,
    "--ring": input.primary,
    "--chart-1": input.primary,
    "--chart-2": input.accent,
    "--sidebar-primary": input.primary,
    "--sidebar-primary-foreground": input.primaryForeground,
    "--sidebar-ring": input.primary,
  };
}

type SeasonPalette = { light: SeasonalVars; dark: SeasonalVars };

// Palettes for each season, in both light and dark mode. Colors are chosen to
// evoke the season while keeping foreground/background contrast readable.
export const SEASON_PALETTES: Record<Exclude<Season, "none">, SeasonPalette> = {
  summer: {
    light: palette({
      primary: "oklch(0.66 0.15 195)",
      primaryForeground: "oklch(0.99 0.01 200)",
      accent: "oklch(0.76 0.16 65)",
      accentForeground: "oklch(0.2 0.03 60)",
    }),
    dark: palette({
      primary: "oklch(0.74 0.14 195)",
      primaryForeground: "oklch(0.15 0.02 200)",
      accent: "oklch(0.8 0.15 65)",
      accentForeground: "oklch(0.15 0.02 60)",
    }),
  },
  autumn: {
    light: palette({
      primary: "oklch(0.58 0.16 50)",
      primaryForeground: "oklch(0.99 0.01 60)",
      accent: "oklch(0.66 0.15 75)",
      accentForeground: "oklch(0.2 0.03 60)",
    }),
    dark: palette({
      primary: "oklch(0.68 0.16 50)",
      primaryForeground: "oklch(0.15 0.02 60)",
      accent: "oklch(0.76 0.14 75)",
      accentForeground: "oklch(0.15 0.02 60)",
    }),
  },
  winter: {
    light: palette({
      primary: "oklch(0.55 0.15 245)",
      primaryForeground: "oklch(0.99 0.01 245)",
      accent: "oklch(0.72 0.08 220)",
      accentForeground: "oklch(0.2 0.03 245)",
    }),
    dark: palette({
      primary: "oklch(0.68 0.15 245)",
      primaryForeground: "oklch(0.14 0.02 245)",
      accent: "oklch(0.8 0.07 220)",
      accentForeground: "oklch(0.14 0.02 245)",
    }),
  },
  christmas: {
    light: palette({
      primary: "oklch(0.52 0.2 25)",
      primaryForeground: "oklch(0.99 0.01 25)",
      accent: "oklch(0.5 0.14 150)",
      accentForeground: "oklch(0.99 0.01 150)",
    }),
    dark: palette({
      primary: "oklch(0.62 0.2 25)",
      primaryForeground: "oklch(0.14 0.02 25)",
      accent: "oklch(0.64 0.14 150)",
      accentForeground: "oklch(0.14 0.02 150)",
    }),
  },
  newyear: {
    light: palette({
      primary: "oklch(0.62 0.15 90)",
      primaryForeground: "oklch(0.2 0.03 90)",
      accent: "oklch(0.5 0.2 300)",
      accentForeground: "oklch(0.99 0.01 300)",
    }),
    dark: palette({
      primary: "oklch(0.78 0.15 90)",
      primaryForeground: "oklch(0.14 0.02 90)",
      accent: "oklch(0.64 0.2 300)",
      accentForeground: "oklch(0.14 0.02 300)",
    }),
  },
  halloween: {
    light: palette({
      primary: "oklch(0.62 0.19 55)",
      primaryForeground: "oklch(0.2 0.03 55)",
      accent: "oklch(0.5 0.18 300)",
      accentForeground: "oklch(0.99 0.01 300)",
    }),
    dark: palette({
      primary: "oklch(0.72 0.19 55)",
      primaryForeground: "oklch(0.14 0.02 55)",
      accent: "oklch(0.62 0.19 300)",
      accentForeground: "oklch(0.14 0.02 300)",
    }),
  },
  valentine: {
    light: palette({
      primary: "oklch(0.6 0.2 350)",
      primaryForeground: "oklch(0.99 0.01 350)",
      accent: "oklch(0.62 0.19 15)",
      accentForeground: "oklch(0.99 0.01 15)",
    }),
    dark: palette({
      primary: "oklch(0.72 0.19 350)",
      primaryForeground: "oklch(0.14 0.02 350)",
      accent: "oklch(0.72 0.18 15)",
      accentForeground: "oklch(0.14 0.02 15)",
    }),
  },
  anniversary: {
    light: palette({
      primary: "oklch(0.594 0.183 256)",
      primaryForeground: "oklch(0.99 0.01 262)",
      accent: "oklch(0.6 0.24 330)",
      accentForeground: "oklch(0.99 0.01 330)",
    }),
    dark: palette({
      primary: "oklch(0.65 0.18 256)",
      primaryForeground: "oklch(0.14 0.02 262)",
      accent: "oklch(0.72 0.22 330)",
      accentForeground: "oklch(0.14 0.02 330)",
    }),
  },
  // Black Friday: bold near-black primary with a hot amber accent for offers.
  blackfriday: {
    light: palette({
      primary: "oklch(0.26 0.02 265)",
      primaryForeground: "oklch(0.98 0.01 90)",
      accent: "oklch(0.78 0.17 75)",
      accentForeground: "oklch(0.2 0.03 60)",
    }),
    dark: palette({
      primary: "oklch(0.9 0.02 90)",
      primaryForeground: "oklch(0.16 0.02 265)",
      accent: "oklch(0.82 0.17 80)",
      accentForeground: "oklch(0.16 0.02 60)",
    }),
  },
  // Blue Monday: cool, moody deep blues with a cyan accent.
  bluemonday: {
    light: palette({
      primary: "oklch(0.45 0.15 265)",
      primaryForeground: "oklch(0.99 0.01 265)",
      accent: "oklch(0.66 0.14 220)",
      accentForeground: "oklch(0.18 0.03 245)",
    }),
    dark: palette({
      primary: "oklch(0.62 0.16 265)",
      primaryForeground: "oklch(0.14 0.02 265)",
      accent: "oklch(0.74 0.13 220)",
      accentForeground: "oklch(0.14 0.02 245)",
    }),
  },
};

export type SeasonalThemeContextValue = {
  // The season currently applied to the site, resolved from the admin-managed
  // configuration in the database.
  season: Season;
};

export const SeasonalThemeContext = createContext<SeasonalThemeContextValue | null>(null);

export function useSeasonalTheme(): SeasonalThemeContextValue {
  const ctx = useContext(SeasonalThemeContext);
  if (!ctx) {
    throw new Error("useSeasonalTheme must be used within a SeasonalThemeProvider");
  }
  return ctx;
}
